/**
 * 📚 SERVICIO DE ANÁLISIS ESTÁTICOS
 * 
 * Análisis pre-escritos de conceptos geopolíticos
 * Reemplaza la funcionalidad de IA con contenido curado
 */

// 🎯 Análisis de Mahan
export const mahanAnalysis = {
  'Guerre de Course': `**Guerre de Course: La Guerra de Corso**

La guerre de course representa una estrategia naval asimétrica donde potencias menores atacan el comercio enemigo mediante corsarios y buques ligeros, evitando el enfrentamiento directo con flotas superiores.

**Análisis Estratégico:**
- **Ventajas**: Bajo costo, alto impacto económico, difícil de contrarrestar
- **Desventajas**: No decide guerras, requiere bases seguras, vulnerable a convoyes protegidos
- **Casos Históricos**: Francia vs Inglaterra (siglo XVIII), Confederados vs Unión (Guerra Civil)

**Relevancia Contemporánea:**
La guerra de corso moderna se manifiesta en guerra submarina, ataques con misiles antibuque y guerra híbrida contra infraestructura marítima.`,

  'Flota en Potencia': `**Flota en Potencia (Fleet in Being)**

Estrategia donde una flota inferior permanece en puerto, amenazando con salir, obligando al enemigo a mantener fuerzas superiores bloqueándola.

**Principios Clave:**
- **Economía de Fuerzas**: Inmoviliza recursos enemigos superiores
- **Negación de Área**: Controla indirectamente zonas marítimas
- **Preservación**: Mantiene capacidad para momento decisivo

**Aplicaciones Históricas:**
- Flota alemana en Kiel (WWI)
- Tirpitz en Noruega (WWII)
- Flota soviética en Guerra Fría

**Limitaciones:**
Requiere puertos fortificados, puede ser neutralizada por bloqueo lejano, riesgo de obsolescencia tecnológica.`,

  'Líneas de Comunicación': `**Líneas de Comunicación Marítimas (SLOCs)**

Las rutas marítimas que conectan territorios, bases y recursos son la columna vertebral del poder naval y económico.

**Elementos Críticos:**
1. **Chokepoints**: Estrechos estratégicos (Malaca, Hormuz, Suez)
2. **Rutas Comerciales**: Flujos de energía, alimentos, manufacturas
3. **Bases Navales**: Puntos de apoyo y reabastecimiento

**Control y Protección:**
- Superioridad naval en zonas clave
- Bases avanzadas para proyección
- Capacidad de convoy y escolta
- Negación de acceso al enemigo

**Impacto Geopolítico:**
El control de SLOCs determina la capacidad de sostener guerras prolongadas y mantener imperios comerciales.`,

  'default': `**Análisis del Poder Naval según Mahan**

Alfred Thayer Mahan revolucionó el pensamiento estratégico al demostrar que el control del mar es fundamental para el poder nacional.

**Principios Fundamentales:**
1. El comercio marítimo es la base de la riqueza nacional
2. Una flota poderosa protege el comercio y proyecta poder
3. Las bases navales extienden el alcance de la flota
4. El control de chokepoints estratégicos es decisivo

**Elementos del Poder Naval:**
- Posición geográfica favorable
- Conformación física (puertos naturales)
- Extensión territorial adecuada
- Población con tradición marítima
- Carácter nacional emprendedor
- Gobierno que apoye el poder naval

**Legado:**
Las ideas de Mahan influyeron en la construcción de flotas modernas y siguen siendo relevantes en la era de la proyección de poder global.`
};

// 🌍 Análisis de Mackinder
export const mackinderAnalysis = {
  'Heartland': `**El Heartland: El Corazón Continental**

El Heartland es la vasta región de Eurasia central, inaccesible al poder naval, que Mackinder identificó como el pivote geográfico de la historia.

**Características Geográficas:**
- Extensión: Europa Oriental + Asia Central
- Inaccesibilidad: Sin salida directa a mares navegables
- Recursos: Vastos territorios, minerales, agricultura

**Tesis Central:**
"Quien gobierne Europa Oriental dominará el Heartland;
Quien gobierne el Heartland dominará la Isla Mundial;
Quien gobierne la Isla Mundial dominará el Mundo"

**Implicaciones Estratégicas:**
1. **Poder Terrestre vs Naval**: El Heartland es inmune al poder naval
2. **Recursos Autosuficientes**: Puede sostener una gran potencia
3. **Posición Central**: Permite proyección en todas direcciones

**Relevancia Actual:**
La teoría explica la importancia geopolítica de Rusia, Asia Central y las tensiones por el control de Europa Oriental.`,

  'Rimland': `**El Rimland: La Zona de Contacto**

Aunque Mackinder enfatizó el Heartland, reconoció la importancia del Rimland (posteriormente desarrollado por Spykman) como zona de contacto entre poder terrestre y naval.

**Definición:**
Franja costera que rodea el Heartland, desde Europa Occidental hasta el Sudeste Asiático.

**Importancia Estratégica:**
- **Zona de Conflicto**: Choque entre poder terrestre y naval
- **Recursos y Población**: Concentración de riqueza y demografía
- **Acceso Marítimo**: Puertos y rutas comerciales

**Dinámica Geopolítica:**
El control del Rimland puede:
1. Contener al Heartland (estrategia occidental)
2. Permitir al Heartland acceso a mares cálidos
3. Crear potencias independientes que equilibren el sistema

**Casos Históricos:**
- Europa como barrera al Heartland ruso
- Japón como potencia Rimland vs China/Rusia
- Oriente Medio como zona de competencia`,

  'Isla Mundial': `**La Isla Mundial (World-Island)**

Concepto de Mackinder que unifica Europa, Asia y África como una masa continental única, el mayor bloque terrestre del planeta.

**Composición:**
- **Heartland**: Núcleo inaccesible (Eurasia Central)
- **Rimland**: Periferia costera
- **África**: Extensión meridional

**Significado Geopolítico:**
1. **Masa Crítica**: Contiene mayoría de población y recursos mundiales
2. **Autosuficiencia Potencial**: Puede ser económicamente independiente
3. **Dominación Global**: Su control implica supremacía mundial

**Teoría de Mackinder:**
Una potencia que unifique la Isla Mundial sería invencible, pues combinaría:
- Recursos del Heartland
- Industria y población del Rimland
- Materias primas de África

**Prevención Histórica:**
Las potencias marítimas (UK, USA) han trabajado para evitar que una sola potencia domine la Isla Mundial mediante:
- Balance de poder en Europa
- Contención de Rusia/URSS
- Alianzas en el Rimland`,

  'Cordón Sanitario': `**Cordón Sanitario**

Estrategia geopolítica de crear una cadena de estados buffer entre una gran potencia y sus adversarios.

**Origen:**
Término usado después de WWI para describir los nuevos estados de Europa Oriental (Polonia, Checoslovaquia, etc.) creados para contener a Rusia soviética.

**Función Estratégica:**
- Prevenir expansión de potencias Heartland
- Crear zona de amortiguamiento
- Fragmentar el poder continental

**Aplicaciones Históricas:**
- Estados bálticos post-WWI
- Europa Oriental durante Guerra Fría
- Repúblicas ex-soviéticas actuales

**Relevancia Actual:**
La expansión de OTAN hacia el este puede verse como un cordón sanitario moderno contra Rusia.`,

  'Lebensraum': `**Lebensraum (Espacio Vital)**

Concepto geopolítico alemán que justificaba la expansión territorial para asegurar recursos y población.

**Origen:**
Desarrollado por geógrafos alemanes, adoptado por el nazismo como justificación para expansión hacia el este.

**Relación con Mackinder:**
Los nazis interpretaron mal a Mackinder, buscando conquistar el Heartland para dominar Eurasia.

**Crítica:**
- Determinismo geográfico extremo
- Justificación pseudocientífica de imperialismo
- Ignoró factores económicos y tecnológicos

**Lección Histórica:**
Demuestra los peligros de aplicar teorías geopolíticas sin consideraciones éticas o realistas.`,

  'Geopolítica Marítima vs Terrestre': `**Geopolítica Marítima vs Terrestre**

Debate fundamental sobre qué tipo de poder es supremo: el naval (marítimo) o el continental (terrestre).

**Poder Marítimo (Mahan):**
- Control de rutas comerciales
- Proyección de poder global
- Flexibilidad estratégica
- Ejemplos: UK, USA, Japón

**Poder Terrestre (Mackinder):**
- Control de recursos continentales
- Masa crítica de población
- Autosuficiencia económica
- Ejemplos: Rusia, China, Alemania

**Síntesis (Spykman):**
El poder supremo combina ambos: control del Rimland permite proyección marítima y terrestre.

**Relevancia Moderna:**
China busca ser potencia híbrida: terrestre (Belt and Road) y marítima (armada moderna).`,

  'default': `**La Teoría del Heartland de Mackinder**

Halford Mackinder propuso que la geografía determina el poder geopolítico, y que el control del "corazón continental" de Eurasia es la clave del dominio mundial.

**Evolución de la Teoría:**
1. **1904 - El Pivote Geográfico**: Identifica el Heartland como zona pivote
2. **1919 - Revisión Post-WWI**: Advierte sobre el peligro alemán-ruso
3. **1943 - Versión Final**: Refina la teoría ante la WWII

**Principios Fundamentales:**
- La geografía es el factor más permanente en geopolítica
- El poder terrestre puede superar al poder naval
- El Heartland es la posición estratégica suprema
- Europa Oriental es la puerta al Heartland

**Aplicaciones Históricas:**
- Expansión rusa hacia Europa y Asia
- Política de contención de la Guerra Fría
- Competencia actual por influencia en Eurasia

**Críticas:**
- Subestima el poder aéreo y espacial
- Ignora factores económicos y tecnológicos
- Determinismo geográfico excesivo

**Legado:**
La teoría sigue siendo fundamental para entender la geopolítica de Eurasia y las estrategias de grandes potencias.`
};

// 🗺️ Análisis de Spykman
export const spykmanAnalysis = {
  'Rimland Theory': `**Teoría del Rimland de Spykman**

Nicholas Spykman modificó la teoría de Mackinder, argumentando que el Rimland (no el Heartland) es la zona geopolítica más importante.

**Tesis Central:**
"Quien controle el Rimland dominará Eurasia;
Quien domine Eurasia controlará los destinos del mundo"

**Características del Rimland:**
- **Ubicación**: Periferia costera de Eurasia
- **Población**: Mayor densidad demográfica
- **Economía**: Centros industriales y comerciales
- **Acceso**: Tanto terrestre como marítimo

**Ventajas Estratégicas:**
1. **Doble Acceso**: Puede proyectar poder por tierra y mar
2. **Recursos**: Concentración de riqueza e industria
3. **Posición**: Puede contener o facilitar expansión del Heartland

**Implicaciones para USA:**
Spykman argumentó que Estados Unidos debe:
- Prevenir que una potencia domine el Rimland
- Mantener balance de poder en Eurasia
- Establecer alianzas en zonas clave del Rimland

**Aplicación Histórica:**
- Política de contención de la Guerra Fría
- Sistema de alianzas (OTAN, SEATO, etc.)
- Presencia militar en Europa, Medio Oriente y Asia`,

  'Containment': `**Estrategia de Contención**

La contención es la aplicación práctica de la teoría de Spykman: prevenir la expansión de una potencia Heartland mediante control del Rimland.

**Principios:**
1. **Negación de Acceso**: Impedir que el Heartland alcance mares cálidos
2. **Alianzas Periféricas**: Crear cadena de estados aliados
3. **Presencia Militar**: Bases en puntos estratégicos del Rimland
4. **Apoyo Económico**: Fortalecer estados del Rimland

**Implementación Guerra Fría:**
- **Europa**: OTAN, Plan Marshall
- **Asia**: Alianzas con Japón, Corea del Sur, Filipinas
- **Medio Oriente**: Pacto de Bagdad, apoyo a Israel

**Zonas Críticas:**
- Europa Occidental (barrera a expansión soviética)
- Turquía (control de estrechos)
- Irán (acceso al Golfo Pérsico)
- Japón (contención en Asia Oriental)

**Éxito Histórico:**
La estrategia de contención logró:
- Prevenir expansión soviética
- Mantener acceso occidental a recursos
- Eventualmente contribuir al colapso de la URSS

**Relevancia Actual:**
Continúa aplicándose en:
- Contención de China en Indo-Pacífico
- Prevención de expansión rusa en Europa Oriental
- Control de chokepoints estratégicos`,

  'default': `**La Visión Geopolítica de Spykman**

Nicholas Spykman desafió a Mackinder al argumentar que el Rimland, no el Heartland, es la zona geopolítica decisiva.

**Diferencias con Mackinder:**
- **Foco**: Rimland vs Heartland
- **Poder**: Marítimo-terrestre vs puramente terrestre
- **Estrategia**: Contención activa vs balance pasivo

**Contribuciones Clave:**
1. Identificó el Rimland como zona crítica
2. Desarrolló la estrategia de contención
3. Influyó en la política exterior estadounidense
4. Anticipó la Guerra Fría

**Legado:**
Las ideas de Spykman formaron la base de la política de contención durante la Guerra Fría y siguen influyendo en la estrategia geopolítica actual.`
};

// 🎓 Análisis de Waltz
export const waltzAnalysis = {
  'Neorealism': `**Neorrealismo Estructural de Waltz**

Kenneth Waltz revolucionó las Relaciones Internacionales al proponer que la estructura del sistema internacional, no las características de los estados, determina su comportamiento.

**Principios Fundamentales:**
1. **Anarquía**: No existe autoridad superior a los estados
2. **Autoayuda**: Cada estado debe garantizar su propia supervivencia
3. **Distribución de Capacidades**: La estructura del poder determina el comportamiento

**Niveles de Análisis:**
- **Primer Nivel**: Individuos (rechazado como explicación)
- **Segundo Nivel**: Estados (insuficiente)
- **Tercer Nivel**: Sistema internacional (determinante)

**Implicaciones:**
- Los estados actúan racionalmente para maximizar seguridad
- El conflicto es inherente al sistema anárquico
- Las alianzas se forman por balance de poder, no ideología
- La cooperación es difícil pero posible

**Críticas:**
- Determinismo estructural excesivo
- Subestima factores domésticos
- Ignora normas e instituciones internacionales`,

  'Balance of Power': `**Balance de Poder según Waltz**

El balance de poder es el mecanismo automático por el cual el sistema internacional mantiene estabilidad.

**Mecanismo:**
1. Estados buscan maximizar su seguridad
2. Poder excesivo de un estado amenaza a otros
3. Estados amenazados forman coaliciones
4. Se restaura el equilibrio

**Tipos de Balance:**
- **Interno**: Aumentar capacidades propias (armamento, economía)
- **Externo**: Formar alianzas con otros estados

**Predicciones:**
- Sistemas bipolares son más estables (Guerra Fría)
- Sistemas multipolares son más inestables (pre-WWI)
- Los estados prefieren balancear que bandwagonear

**Evidencia Histórica:**
- Coaliciones contra Napoleón
- Alianzas en WWI y WWII
- Contención de la URSS en Guerra Fría

**Relevancia Actual:**
Explica la formación de alianzas contra China y la reconfiguración del orden internacional.`,

  'default': `**Teoría Neorrealista de Kenneth Waltz**

Waltz argumenta que la estructura anárquica del sistema internacional, no las características de los estados, determina su comportamiento.

**Conceptos Clave:**
- **Anarquía**: Ausencia de gobierno mundial
- **Autoayuda**: Cada estado por sí mismo
- **Balance de Poder**: Mecanismo estabilizador
- **Polaridad**: Número de grandes potencias

**Contribución:**
Waltz proporcionó una teoría parsimoniosa y científica de las relaciones internacionales, influyendo profundamente en la disciplina y en el análisis de política exterior.`
};

// 🛡️ Análisis de Seguridad Nacional
export const securityAnalysis = {
  'Defensa en Capas': `**Sistema de Defensa en Capas**

La defensa moderna requiere múltiples niveles de protección, desde el espacio hasta el ciberespacio.

**Capas Defensivas:**
1. **Espacial**: Satélites, alerta temprana
2. **Estratosférica**: Defensa antimisiles
3. **Aérea**: Superioridad aérea, interceptores
4. **Terrestre**: Fuerzas convencionales
5. **Marítima**: Control de mares
6. **Cibernética**: Protección de infraestructura digital

**Principios:**
- **Redundancia**: Múltiples sistemas de respaldo
- **Integración**: Coordinación entre capas
- **Profundidad**: Defensa escalonada

**Desafíos Modernos:**
- Armas hipersónicas
- Guerra híbrida
- Amenazas asimétricas
- Ciberataques`,

  'Disuasión': `**Estrategia de Disuasión**

La disuasión busca prevenir agresiones mediante la amenaza creíble de represalia inaceptable.

**Tipos:**
- **Disuasión Nuclear**: Destrucción mutua asegurada
- **Disuasión Convencional**: Capacidad de defensa y contraataque
- **Disuasión Extendida**: Protección de aliados

**Requisitos:**
1. **Capacidad**: Poder real de represalia
2. **Credibilidad**: Voluntad de usar la fuerza
3. **Comunicación**: Señales claras al adversario

**Aplicaciones:**
- Doctrina nuclear de Guerra Fría
- Defensa de aliados (OTAN, Japón, Corea del Sur)
- Prevención de conflictos regionales`,

  'default': `**Análisis de Seguridad Nacional**

La seguridad nacional moderna requiere un enfoque integral que combine capacidades militares, económicas, diplomáticas y tecnológicas.

**Dimensiones:**
- **Militar**: Defensa territorial y proyección de poder
- **Económica**: Protección de intereses comerciales
- **Energética**: Seguridad de suministros
- **Cibernética**: Protección de infraestructura digital
- **Informacional**: Contra desinformación

**Desafíos del Siglo XXI:**
- Amenazas transnacionales (terrorismo, crimen organizado)
- Competencia entre grandes potencias
- Proliferación de armas avanzadas
- Vulnerabilidades cibernéticas
- Cambio climático y recursos

**Estrategias:**
- Alianzas y cooperación internacional
- Inversión en tecnologías avanzadas
- Resiliencia de infraestructura crítica
- Inteligencia y prevención`
};

/**
 * Obtener análisis estático basado en el tema
 */
export const getStaticAnalysis = (category, topic) => {
  const analyses = {
    mahan: mahanAnalysis,
    mackinder: mackinderAnalysis,
    spykman: spykmanAnalysis,
    waltz: waltzAnalysis,
    security: securityAnalysis
  };

  const categoryAnalysis = analyses[category?.toLowerCase()];
  
  // Si no hay categoría o no existe, usar análisis general
  if (!categoryAnalysis) {
    return getGeneralAnalysis(topic);
  }

  return categoryAnalysis[topic] || categoryAnalysis['default'];
};

/**
 * Análisis general para cualquier tema
 */
const getGeneralAnalysis = (topic) => {
  // Análisis genérico basado en palabras clave
  const topicLower = (topic || '').toLowerCase();
  
  if (topicLower.includes('heartland') || topicLower.includes('mackinder')) {
    return mackinderAnalysis['default'];
  }
  
  if (topicLower.includes('rimland') || topicLower.includes('spykman') || topicLower.includes('contención')) {
    return spykmanAnalysis['default'];
  }
  
  if (topicLower.includes('naval') || topicLower.includes('mahan') || topicLower.includes('mar')) {
    return mahanAnalysis['default'];
  }
  
  if (topicLower.includes('waltz') || topicLower.includes('neorrealismo') || topicLower.includes('balance')) {
    return waltzAnalysis['default'];
  }
  
  if (topicLower.includes('seguridad') || topicLower.includes('defensa') || topicLower.includes('disuasión')) {
    return securityAnalysis['default'];
  }
  
  // Análisis completamente genérico
  return `**Análisis Geopolítico**

El análisis geopolítico requiere considerar múltiples factores que interactúan en el sistema internacional.

**Dimensiones Clave:**

1. **Geografía**: La ubicación, recursos naturales y características físicas determinan oportunidades y limitaciones estratégicas.

2. **Poder**: La distribución de capacidades militares, económicas y tecnológicas entre estados configura el balance de poder.

3. **Intereses**: Los objetivos nacionales, tanto de seguridad como económicos, motivan el comportamiento de los estados.

4. **Instituciones**: Organizaciones internacionales, alianzas y normas influyen en la cooperación y el conflicto.

**Teorías Fundamentales:**

- **Realismo**: Enfatiza el poder, la anarquía internacional y la competencia entre estados
- **Geopolítica Clásica**: Destaca la importancia de la geografía (Mackinder, Mahan, Spykman)
- **Liberalismo**: Resalta la cooperación, instituciones y interdependencia económica
- **Constructivismo**: Considera ideas, identidades y normas sociales

**Aplicación Práctica:**

Para analizar cualquier situación geopolítica:
1. Identificar actores principales y sus intereses
2. Evaluar distribución de poder y capacidades
3. Considerar factores geográficos y recursos
4. Analizar alianzas y relaciones internacionales
5. Proyectar escenarios futuros posibles

**Conclusión:**

El análisis geopolítico efectivo requiere una perspectiva multidimensional que integre geografía, poder, historia y teoría para comprender la dinámica del sistema internacional.`;
};

/**
 * Simular delay de "análisis" para mejor UX
 */
export const simulateAnalysis = async (category, topic) => {
  // Simular tiempo de procesamiento (500-1500ms)
  const delay = 500 + Math.random() * 1000;
  await new Promise(resolve => setTimeout(resolve, delay));
  
  return getStaticAnalysis(category, topic);
};
